import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { BatteryFull, Car, Flag, Signal, Wifi } from 'lucide-react';
import { SUBPARTS } from './slides';

const now = () => new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

export function StatusBar({ dark }: { dark?: boolean }) {
  const [t, setT] = useState(now());
  useEffect(() => { const id = setInterval(() => setT(now()), 15000); return () => clearInterval(id); }, []);
  return (
    <div className={`absolute inset-x-0 top-0 z-20 flex h-[44px] items-center justify-between px-12 font-ui text-[17px] font-semibold ${dark ? 'text-white' : 'text-neutral-900'}`} data-testid="phone-status-bar">
      <span>{t}</span>
      <div className="flex items-center gap-3"><Signal size={18} /><span className="text-[14px]">5G</span><Wifi size={18} /><BatteryFull size={24} /></div>
    </div>
  );
}

export function RideBar({ current }: { current: number }) {
  const stops = ['Départ', ...SUBPARTS.map((s) => s.part!), 'Verdict'];
  const pos = current + 1;
  const pct = (i: number) => (i / (stops.length - 1)) * 100;
  return (
    <div className="glass absolute left-16 right-16 top-[58px] z-20 flex h-[66px] items-center gap-8 rounded-2xl border border-white/60 px-6 shadow-[0_10px_30px_-15px_rgba(0,0,0,0.35)]" data-testid="ride-progress-bar">
      <div className="shrink-0 font-app">
        <div className="flex items-center gap-2 text-[13px] font-semibold uppercase tracking-wider text-blue-600"><span className="h-2 w-2 animate-pulse rounded-full bg-blue-600" /> Course en cours</div>
        <div className="text-[17px] font-bold text-neutral-900">Étape {current + 1}/6 · {stops[pos]}</div>
      </div>
      <div className="relative mx-4 h-full flex-1">
        <div className="absolute left-0 right-0 top-1/2 h-[5px] -translate-y-1/2 rounded-full bg-neutral-200" />
        <motion.div className="absolute left-0 top-1/2 h-[5px] -translate-y-1/2 rounded-full bg-blue-600" initial={{ width: `${pct(pos - 1)}%` }} animate={{ width: `${pct(pos)}%` }} transition={{ duration: 0.7, ease: 'easeOut' }} />
        {stops.map((s, i) => (
          <div key={s} className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2" style={{ left: `${pct(i)}%` }}>
            <div className={`h-3.5 w-3.5 rounded-full border-[3px] ${i <= pos ? 'border-blue-600 bg-white' : 'border-neutral-300 bg-white'}`} />
            <span className={`absolute left-1/2 top-4 -translate-x-1/2 whitespace-nowrap font-ui text-[11px] font-semibold ${i === pos ? 'text-neutral-900' : 'text-neutral-400'}`}>{s}</span>
          </div>
        ))}
        <motion.div className="absolute top-1/2 z-10 -translate-x-1/2 -translate-y-[130%] rounded-full bg-black p-1.5 text-white shadow-lg" initial={{ left: `${pct(pos - 1)}%` }} animate={{ left: `${pct(pos)}%` }} transition={{ duration: 0.7, ease: 'easeOut' }}>
          <Car size={15} />
        </motion.div>
        <Flag size={16} className="absolute right-0 top-1/2 -translate-y-[150%] translate-x-1/2 text-neutral-400" />
      </div>
    </div>
  );
}

export function MapBg({ dark, route = true }: { dark?: boolean; route?: boolean }) {
  const c = dark
    ? { bg: '#111214', block: '#1a1c20', road: '#26292f', minor: '#1f2227', park: '#15231a', water: '#0f1a2a' }
    : { bg: '#eef0f2', block: '#e3e6ea', road: '#ffffff', minor: '#f7f8f9', park: '#d9ecd9', water: '#cfe0f5' };
  return (
    <svg className="absolute inset-0 h-full w-full" viewBox="0 0 1600 900" preserveAspectRatio="xMidYMid slice" aria-hidden>
      <rect width="1600" height="900" fill={c.bg} />
      <path d="M-20 720 C 300 650, 500 800, 820 740 S 1350 640, 1640 700 L1640 920 L-20 920 Z" fill={c.water} />
      <rect x="1180" y="120" width="300" height="200" rx="24" fill={c.park} />
      <rect x="120" y="300" width="220" height="160" rx="18" fill={c.park} />
      {[[60, 90, 260, 170], [380, 90, 320, 170], [760, 90, 360, 170], [380, 330, 320, 210], [760, 330, 360, 210], [1180, 380, 300, 180]].map(([x, y, w, h], i) => (
        <rect key={`${x}-${y}`} x={x} y={y} width={w} height={h} rx="14" fill={c.block} />
      ))}
      <g stroke={c.minor} strokeWidth="10" fill="none" strokeLinecap="round">
        <path d="M0 300 H1600" /><path d="M560 0 V900" /><path d="M1140 0 V900" /><path d="M0 590 H1600" />
      </g>
      <g stroke={c.road} strokeWidth="22" fill="none" strokeLinecap="round">
        <path d="M0 270 C 400 250, 700 300, 1600 250" /><path d="M350 0 C 360 300, 330 600, 380 900" />
        <path d="M740 0 L 740 900" /><path d="M0 560 C 500 540, 1100 600, 1600 540" /><path d="M1500 0 C 1300 300, 1200 600, 1160 900" />
      </g>
      {route && (
        <>
          <motion.path d="M200 820 C 260 600, 350 560, 380 400 S 700 300, 740 260 S 1100 230, 1320 180" stroke="#2563eb" strokeWidth="9" fill="none" strokeLinecap="round" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.9, delay: 0.55, ease: 'easeInOut' }} opacity={dark ? 0.9 : 0.75} />
          <circle cx="1320" cy="180" r="14" fill="#111" stroke="#fff" strokeWidth="5" />
        </>
      )}
    </svg>
  );
}
